<template>
  <div class="mt-3">
    <div class="card flat-card mb-3">
      <div class="card-body p-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 class="mb-0 section-title">Dukakit Staff</h6>
            <small class="text-secondary">Users with internal dashboard access. Assign roles to control what each person can see and do.</small>
          </div>
          <button class="btn btn-primary" @click="openAddModal">
            <i class="bi bi-person-plus mr-1"></i> Add Staff Member
          </button>
        </div>

        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>

        <table v-else class="table table-hover mb-0">
          <thead class="thead-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Internal Role</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="member in staff" :key="member.id">
              <td class="align-middle font-weight-bold" style="font-size:.85rem;">
                {{ member.full_name || member.name }}
              </td>
              <td class="align-middle text-secondary" style="font-size:.85rem;">{{ member.email }}</td>
              <td class="align-middle">
                <span v-if="member.dukakit_role" :class="['badge', roleBadge(member.dukakit_role)]">
                  {{ member.dukakit_role }}
                </span>
                <span v-else class="badge badge-secondary">No role</span>
              </td>
              <td class="align-middle text-right">
                <button class="btn btn-outline-secondary mr-1" @click="openEditModal(member)">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-outline-danger" @click="revokeAccess(member)" :disabled="member.saving">
                  <i class="bi bi-x"></i>
                </button>
              </td>
            </tr>
            <tr v-if="staff.length === 0">
              <td colspan="4" class="text-center text-secondary py-3">No internal staff configured</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add / Edit modal -->
    <b-modal ref="staffModal" :title="editMode ? 'Edit Staff Role' : 'Add Staff Member'" hide-footer>
      <form @submit.prevent="submitStaff">
        <div v-if="!editMode" class="form-group">
          <label class="font-weight-bold">User Email</label>
          <input
            v-model="form.email"
            class="form-control"
            placeholder="Enter email of existing user…"
            type="email"
            required
          />
          <small class="text-muted">The user must already have an account. Their <code>isDukakit</code> flag will be set automatically.</small>
        </div>
        <div v-else class="form-group">
          <p class="mb-1 font-weight-bold">{{ form.name }}</p>
          <small class="text-muted">{{ form.email }}</small>
        </div>
        <div class="form-group mt-3">
          <label class="font-weight-bold">Internal Role</label>
          <select v-model="form.role" class="form-control" required>
            <option value="">Select role…</option>
            <option v-for="r in availableRoles" :key="r.value" :value="r.value">
              {{ r.label }}
            </option>
          </select>
          <small class="text-muted d-block mt-1">{{ roleDescription(form.role) }}</small>
        </div>
        <button class="btn btn-primary btn-block mt-3" type="submit" :disabled="form.saving">
          <span v-if="form.saving" class="spinner-border spinner-border-sm mr-1"></span>
          {{ editMode ? 'Update Role' : 'Grant Access' }}
        </button>
      </form>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

const ROLES = [
  {
    value: 'dukakit-super-admin',
    label: 'Super Admin',
    description: 'Full access — all sections, all actions.',
  },
  {
    value: 'dukakit-operations',
    label: 'Operations',
    description: 'Company management, affiliate assignment, impersonation. No billing or revenue data.',
  },
  {
    value: 'dukakit-support',
    label: 'Support',
    description: 'Read-only company view + feedback management. No destructive actions.',
  },
  {
    value: 'dukakit-finance',
    label: 'Finance',
    description: 'Billing date changes, subscription plan changes, revenue tab.',
  },
  {
    value: 'dukakit-analytics',
    label: 'Analytics',
    description: 'Read-only dashboards and charts.',
  },
];

export default {
  name: 'AdminStaffManager',

  data() {
    return {
      loading: false,
      staff: [],
      editMode: false,
      form: {
        id: null,
        email: '',
        name: '',
        role: '',
        saving: false,
      },
    };
  },

  computed: {
    availableRoles() {
      return ROLES;
    },
  },

  mounted() {
    this.fetchStaff();
  },

  methods: {
    async fetchStaff() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/staff');
        this.staff = data;
      } finally {
        this.loading = false;
      }
    },

    openAddModal() {
      this.editMode    = false;
      this.form        = { id: null, email: '', name: '', role: '', saving: false };
      this.$refs.staffModal.show();
    },

    openEditModal(member) {
      this.editMode = true;
      this.form     = {
        id:     member.id,
        email:  member.email,
        name:   member.full_name || member.name,
        role:   member.dukakit_role || '',
        saving: false,
      };
      this.$refs.staffModal.show();
    },

    async submitStaff() {
      this.form.saving = true;
      try {
        await axios.post('/api/admin/staff', {
          email: this.form.email,
          role:  this.form.role,
          id:    this.form.id,
        });
        this.$refs.staffModal.hide();
        this.$toasts.push({ message: 'Staff updated successfully', type: 'success' });
        this.fetchStaff();
      } catch (e) {
        this.$toasts.push({
          message: e.response?.data?.message || 'Failed to update staff',
          type: 'error',
        });
      } finally {
        this.form.saving = false;
      }
    },

    async revokeAccess(member) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove internal access for ${member.full_name || member.name}?`,
        { title: 'Revoke Access', okVariant: 'danger', okTitle: 'Revoke', centered: true }
      );
      if (!confirmed) return;

      await axios.delete('/api/admin/staff/' + member.id);
      this.$toasts.push({ message: 'Access revoked', type: 'success' });
      this.fetchStaff();
    },

    roleBadge(role) {
      const map = {
        'dukakit-super-admin':  'badge-danger',
        'dukakit-operations':   'badge-primary',
        'dukakit-support':      'badge-dark',
        'dukakit-finance':      'badge-dark',
        'dukakit-analytics':    'badge-secondary',
      };
      return map[role] || 'badge-light';
    },

    roleDescription(role) {
      const found = ROLES.find(r => r.value === role);
      return found ? found.description : '';
    },
  },
};
</script>

<style scoped>
.flat-card {
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
.section-title {
  color: #0f172a;
  font-weight: 600;
}
</style>
